/**
 * Centralized permission keys used throughout the application
 * 
 * Usage in templates:
 *   *hasAccess="PERMISSIONS.APPLICATIONS_PORTFOLIO_WRITE"
 *   *ngIf="PERMISSIONS.ASSESSMENT_MAIN_WRITE | hasAccess"
 * 
 * Usage in TypeScript:
 *   import { PERMISSIONS } from '@app/constants/permissions.constants';
 *   this.userContextService.hasAccess(PERMISSIONS.APPLICATIONS_PORTFOLIO_WRITE);
 */
export const PERMISSIONS = {
  // Dashboard.Main.Page
  DASHBOARD_MAIN_READ: 'Dashboard.Main.Page:Read',
  DASHBOARD_MAIN_WRITE: 'Dashboard.Main.Page:Write',

  // Applications.Portfolio.Page
  APPLICATIONS_PORTFOLIO_READ: 'Applications.Portfolio.Page:Read',
  APPLICATIONS_PORTFOLIO_WRITE: 'Applications.Portfolio.Page:Write',

  // Assessment.Main.Page
  ASSESSMENT_MAIN_READ: 'Assessment.Main.Page:Read',
  ASSESSMENT_MAIN_WRITE: 'Assessment.Main.Page:Write',

  // Admin.Main.Page
  ADMIN_MAIN_READ: 'Admin.Main.Page:Read',
  ADMIN_MAIN_WRITE: 'Admin.Main.Page:Write',

  // Admin.RolesPrivileges.Page
  ADMIN_ROLES_PRIVILEGES_READ: 'Admin.RolesPrivileges.Page:Read',
  ADMIN_ROLES_PRIVILEGES_WRITE: 'Admin.RolesPrivileges.Page:Write',

  // Admin.SizingCriteria.Page
  ADMIN_SIZING_CRITERIA_READ: 'Admin.SizingCriteria.Page:Read',
  ADMIN_SIZING_CRITERIA_WRITE: 'Admin.SizingCriteria.Page:Write',

  // Admin.AIConfig.Page
  ADMIN_AI_CONFIG_READ: 'Admin.AIConfig.Page:Read',
  ADMIN_AI_CONFIG_WRITE: 'Admin.AIConfig.Page:Write',

  // Admin.CmdbSync.Page
  ADMIN_CMDB_SYNC_READ: 'Admin.CmdbSync.Page:Read',
  ADMIN_CMDB_SYNC_WRITE: 'Admin.CmdbSync.Page:Write',

  // Admin.PodsList.Page
  ADMIN_PODS_LIST_READ: 'Admin.PodsList.Page:Read',
  ADMIN_PODS_LIST_WRITE: 'Admin.PodsList.Page:Write',

  // Help.Main.Page
  HELP_MAIN_READ: 'Help.Main.Page:Read',
  HELP_MAIN_WRITE: 'Help.Main.Page:Write',

  // Reports.Main.Page
  REPORTS_MAIN_READ: 'Reports.Main.Page:Read',
  REPORTS_MAIN_WRITE: 'Reports.Main.Page:Write',

  // Reports.Export.Feature
  REPORTS_EXPORT_READ: 'Reports.Export.Feature:Read',
  REPORTS_EXPORT_WRITE: 'Reports.Export.Feature:Write',
} as const;

/**
 * Permission keys without access type suffix (base keys)
 * Used in route configuration and other places where access type is specified separately
 */
export const PERMISSION_KEYS = {
  DASHBOARD_MAIN: 'Dashboard.Main.Page',
  APPLICATIONS_PORTFOLIO: 'Applications.Portfolio.Page',
  ASSESSMENT_MAIN: 'Assessment.Main.Page',
  ADMIN_MAIN: 'Admin.Main.Page',
  ADMIN_ROLES_PRIVILEGES: 'Admin.RolesPrivileges.Page',
  ADMIN_SIZING_CRITERIA: 'Admin.SizingCriteria.Page',
  ADMIN_AI_CONFIG: 'Admin.AIConfig.Page',
  ADMIN_CMDB_SYNC: 'Admin.CmdbSync.Page',
  ADMIN_PODS_LIST: 'Admin.PodsList.Page',
  ADMIN_PLATFORM_MASTER: 'Admin.PlatformMaster.Page',
  HELP_MAIN: 'Help.Main.Page',
  REPORTS_MAIN: 'Reports.Main.Page',
  REPORTS_EXPORT: 'Reports.Export.Feature',
} as const;

/**
 * Type-safe permission key type
 */
export type PermissionKey = typeof PERMISSIONS[keyof typeof PERMISSIONS];
export type BasePermissionKey = typeof PERMISSION_KEYS[keyof typeof PERMISSION_KEYS];
